import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHelmet from '@/components/feature/SEOHelmet';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug = '' } = useParams();
  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <>
        <SEOHelmet title="资源未找到 - 宅宅资源库" description="您访问的资源不存在或已被移除" keywords="宅宅资源库" />
        <Navbar />
        <div className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">资源未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的资源不存在或已被移除</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-150 cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const currentCategory = categories.find(c => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article.relatedIds);

  return (
    <>
      <SEOHelmet
        title={`${article.title} - 宅宅资源库`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},宅宅资源库,二次元资源,${currentCategory?.name || ''}`}
        canonicalUrl={`https://doujincenter.github.io/article/${slug}`}
        ogImage={article.image}
        ogType="article"
      />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        author: { '@type': 'Organization', name: '宅宅资源库' },
        publisher: { '@type': 'Organization', name: '宅宅资源库' },
      }} />

      <Navbar />
      <div className="pt-16">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: article.category, to: `/category/${article.category}` },
          { label: article.title },
        ]} />
      </div>

      <main>
        <article className="w-full px-4 md:px-6 py-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            {/* Article Header */}
            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-500 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-download-line"></i> {article.downloads} 下载
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i> {article.rating}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {article.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            {/* Cover Image */}
            <div className="w-full aspect-video rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
                title={article.title}
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-sm max-w-none mb-10 text-foreground-800 leading-relaxed [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-foreground-800 [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3 [&_p]:text-foreground-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Download Info */}
            <div className="bg-background-100 rounded-xl p-5 mb-10 border border-background-200/70">
              <h3 className="text-base font-bold text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-download-cloud-2-line text-primary-500"></i>
                下载信息
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-foreground-500">文件大小</span>
                  <p className="font-medium text-foreground-800">{article.size}</p>
                </div>
                <div>
                  <span className="text-foreground-500">下载次数</span>
                  <p className="font-medium text-foreground-800">{article.downloads}</p>
                </div>
                <div>
                  <span className="text-foreground-500">发布日期</span>
                  <p className="font-medium text-foreground-800">{article.date}</p>
                </div>
                <div>
                  <span className="text-foreground-500">评分</span>
                  <p className="font-medium text-accent-600">{article.rating} / 10</p>
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-150 cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line mr-1.5"></i>百度网盘
                </button>
                <button className="px-6 py-2.5 rounded-lg bg-background-50 text-foreground-700 text-sm font-medium border border-background-300 hover:bg-background-100 transition-colors duration-150 cursor-pointer whitespace-nowrap">
                  <i className="ri-cloud-line mr-1.5"></i>阿里云盘
                </button>
                <button className="px-6 py-2.5 rounded-lg bg-background-50 text-foreground-700 text-sm font-medium border border-background-300 hover:bg-background-100 transition-colors duration-150 cursor-pointer whitespace-nowrap">
                  <i className="ri-links-line mr-1.5"></i>OneDrive
                </button>
              </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="border-t border-background-200 pt-8">
                <h3 className="text-lg font-bold text-foreground-900 mb-5">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((rel: any) => rel && (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-full aspect-video overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          title={`${rel.title} - 宅宅资源库`}
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors duration-150">
                          {rel.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}